import { AssociatedContact } from '@app/models/AssociatedContact'
import { AssociatedProperty } from '@app/models/AssociatedProperty'
import { IProperty } from '@app/models/Property'
import { ISignTransfer, SignTransfer } from '@app/models/SignTransfer'
import { ISignType, SignType } from '@app/models/SignType'
import { ApplicationState } from '@app/store'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { EditorState } from 'draft-js'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ISignTransferState {
  readonly error?: string
  readonly isOpen: boolean
  readonly isSaving: boolean
  readonly maxQuantity: number
  readonly signTransfer?: ISignTransfer
}

const SignTransferStateRecord = Record({
  error: undefined,
  isOpen: false,
  isSaving: false,
  maxQuantity: 0,
  signTransfer: new SignTransfer(),
})

export class SignTransferState extends SignTransferStateRecord
  implements ISignTransferState {
  public static fromJS(initialState?: Partial<ISignTransferState>) {
    return new SignTransferState(initialState)
  }

  public readonly error?: string
  public readonly isOpen: boolean
  public readonly isSaving: boolean
  public readonly maxQuantity: number
  public readonly signTransfer: ISignTransfer

  constructor(props?: Partial<SignTransferState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  CloseSignTransferModal = 'SIGN_TRANSFER::CLOSE_MODAL',
  DeleteSignTransfer = 'SIGN_TRANSFER::DELETE',
  DeleteSignTransferFailure = 'SIGN_TRANSFER::DELETE_FAILURE',
  DeleteSignTransferRequest = 'SIGN_TRANSFER::DELETE_REQUEST',
  DeleteSignTransferSuccess = 'SIGN_TRANSFER::DELETE_SUCCESS',
  ResetState = 'SIGN_TRANSFER::RESET_STATE',
  SaveSignTransfer = 'SIGN_TRANSFER::SAVE',
  SaveSignTransferFailure = 'SIGN_TRANSFER::SAVE_FAILURE',
  SaveSignTransferRequest = 'SIGN_TRANSFER::SAVE_REQUEST',
  SaveSignTransferSuccess = 'SIGN_TRANSFER::SAVE_SUCCESS',
  SetAssignedTo = 'SIGN_TRANSFER::SET_ASSIGNED_TO',
  SetDueDate = 'SIGN_TRANSFER::SET_DUE_DATE',
  SetNotes = 'SIGN_TRANSFER::SET_NOTES',
  SetQuantity = 'SIGN_TRANSFER::SET_QUANTITY',
  SetTo = 'SIGN_TRANSFER::SET_TO',
  ShowSignTransferModal = 'SIGN_TRANSFER::SHOW_MODAL',
}

interface ICloseSignTransferModalAction {
  type: ActionTypes.CloseSignTransferModal
}

interface IDeleteSignTransferAction {
  signTransfer: ISignTransfer
  type: ActionTypes.DeleteSignTransfer
}

interface IDeleteSignTransferFailureAction {
  error: string
  type: ActionTypes.DeleteSignTransferFailure
}

interface IDeleteSignTransferRequestAction {
  type: ActionTypes.DeleteSignTransferRequest
}

export interface IDeleteSignTransferSuccessAction {
  signTransfer: ISignTransfer
  type: ActionTypes.DeleteSignTransferSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveSignTransferAction {
  signTransfer: SignTransfer
  type: ActionTypes.SaveSignTransfer
}

interface ISaveSignTransferFailureAction {
  error: string
  type: ActionTypes.SaveSignTransferFailure
}

interface ISaveSignTransferRequestAction {
  type: ActionTypes.SaveSignTransferRequest
}

export interface ISaveSignTransferSuccessAction {
  signTransfer: SignTransfer
  type: ActionTypes.SaveSignTransferSuccess
}

interface ISetAssignedToAction {
  assignedTo: List<AssociatedContact>
  type: ActionTypes.SetAssignedTo
}

interface ISetDueDateAction {
  dueDate?: number
  type: ActionTypes.SetDueDate
}

interface ISetNotesAction {
  notes: EditorState
  type: ActionTypes.SetNotes
}

interface ISetQuantityAction {
  quantity: number
  type: ActionTypes.SetQuantity
}

interface ISetToAction {
  to?: AssociatedProperty
  type: ActionTypes.SetTo
}

interface IShowSignTransferModalAction {
  from: IProperty
  quantity: number
  signId: string
  signType: ISignType
  type: ActionTypes.ShowSignTransferModal
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseSignTransferModalAction
  | IDeleteSignTransferFailureAction
  | IDeleteSignTransferRequestAction
  | IDeleteSignTransferSuccessAction
  | IResetStateAction
  | ISaveSignTransferFailureAction
  | ISaveSignTransferRequestAction
  | ISaveSignTransferSuccessAction
  | ISetAssignedToAction
  | ISetDueDateAction
  | ISetNotesAction
  | ISetQuantityAction
  | ISetToAction
  | IShowSignTransferModalAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeSignTransferModal(): ICloseSignTransferModalAction {
  return { type: ActionTypes.CloseSignTransferModal }
}

function deleteSignTransfer(
  signTransfer: ISignTransfer,
): IDeleteSignTransferAction {
  return {
    signTransfer,
    type: ActionTypes.DeleteSignTransfer,
  }
}

function deleteSignTransferFailure(
  error: string,
): IDeleteSignTransferFailureAction {
  return {
    error,
    type: ActionTypes.DeleteSignTransferFailure,
  }
}

function deleteSignTransferRequest(): IDeleteSignTransferRequestAction {
  return { type: ActionTypes.DeleteSignTransferRequest }
}

function deleteSignTransferSuccess(
  signTransfer: ISignTransfer,
): IDeleteSignTransferSuccessAction {
  return {
    signTransfer,
    type: ActionTypes.DeleteSignTransferSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveSignTransfer(signTransfer: SignTransfer): ISaveSignTransferAction {
  return {
    signTransfer,
    type: ActionTypes.SaveSignTransfer,
  }
}

function saveSignTransferFailure(
  error: string,
): ISaveSignTransferFailureAction {
  return {
    error,
    type: ActionTypes.SaveSignTransferFailure,
  }
}

function saveSignTransferRequest(): ISaveSignTransferRequestAction {
  return { type: ActionTypes.SaveSignTransferRequest }
}

function saveSignTransferSuccess(
  signTransfer: SignTransfer,
): ISaveSignTransferSuccessAction {
  return {
    signTransfer,
    type: ActionTypes.SaveSignTransferSuccess,
  }
}

function setAssignedTo(
  assignedTo: List<AssociatedContact>,
): ISetAssignedToAction {
  return {
    assignedTo,
    type: ActionTypes.SetAssignedTo,
  }
}

function setDueDate(dueDate?: number): ISetDueDateAction {
  return {
    dueDate,
    type: ActionTypes.SetDueDate,
  }
}

function setNotes(notes: EditorState): ISetNotesAction {
  return {
    notes,
    type: ActionTypes.SetNotes,
  }
}

function setQuantity(quantity: number): ISetQuantityAction {
  return {
    quantity,
    type: ActionTypes.SetQuantity,
  }
}

function setTo(to?: AssociatedProperty): ISetToAction {
  return {
    to,
    type: ActionTypes.SetTo,
  }
}

function showSignTransferModal(
  from: IProperty,
  signId: string,
  signType: ISignType,
  quantity: number,
): IShowSignTransferModalAction {
  return {
    from,
    quantity,
    signId,
    signType,
    type: ActionTypes.ShowSignTransferModal,
  }
}

export const actionCreators = {
  closeSignTransferModal,
  resetState,
  saveSignTransfer,
  setAssignedTo,
  setDueDate,
  setNotes,
  setQuantity,
  setTo,
}

export const externalActionCreators = {
  deleteSignTransfer,
  showSignTransferModal,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SignTransferState> = (
  state: SignTransferState = new SignTransferState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.CloseSignTransferModal:
      return state.set('isOpen', false) as SignTransferState

    case ActionTypes.DeleteSignTransferFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as SignTransferState

    case ActionTypes.DeleteSignTransferRequest:
      return state.set('isSaving', true) as SignTransferState

    case ActionTypes.DeleteSignTransferSuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false) as SignTransferState

    case ActionTypes.ResetState:
      return new SignTransferState()

    case ActionTypes.SaveSignTransferFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as SignTransferState

    case ActionTypes.SaveSignTransferRequest:
      return state.set('isSaving', true) as SignTransferState

    case ActionTypes.SaveSignTransferSuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false) as SignTransferState

    case ActionTypes.SetAssignedTo:
      return state.setIn(
        ['signTransfer', 'assignment', 'assignedTo'],
        action.assignedTo,
      ) as SignTransferState

    case ActionTypes.SetDueDate:
      return state.setIn(
        ['signTransfer', 'assignment', 'dueDate'],
        action.dueDate,
      ) as SignTransferState

    case ActionTypes.SetNotes:
      return state.setIn(
        ['signTransfer', 'assignment', 'markupState'],
        action.notes,
      ) as SignTransferState

    case ActionTypes.SetQuantity:
      return state.setIn(
        ['signTransfer', 'quantity'],
        action.quantity,
      ) as SignTransferState

    case ActionTypes.SetTo:
      return state.setIn(
        ['signTransfer', 'to'],
        action.to,
      ) as SignTransferState

    case ActionTypes.ShowSignTransferModal: {
      const signTransfer = new SignTransfer({
        from: AssociatedProperty.fromProperty(action.from),
        quantity: action.quantity,
        signId: action.signId,
        signType: SignType.fromJS(action.signType),
      })
      return state
        .set('isOpen', true)
        .set('maxQuantity', action.quantity)
        .set('signTransfer', signTransfer) as SignTransferState
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteSignTransferSaga(action: IDeleteSignTransferAction) {
  try {
    yield put(deleteSignTransferRequest())

    const url = `/api/Assignments/${action.signTransfer.assignmentId}`

    const result = yield makeAuthenticatedRequest(url, {
      method: RequestMethod.DELETE,
    })

    if (result.success === true) {
      yield put(deleteSignTransferSuccess(action.signTransfer))
      const msg = `Deleted transfer of ${action.signTransfer.quantity} - ${
        action.signTransfer.signType.name
      } sign(s)`
      yield put(successToast('Success!', msg))
    } else {
      yield put(deleteSignTransferFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteSignTransferFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveSignTransferSaga(action: ISaveSignTransferAction) {
  try {
    yield put(saveSignTransferRequest())

    const signTransfer = action.signTransfer.toJS()

    signTransfer.assignment.markup = convertToMarkup(
      action.signTransfer.assignment.markupState.getCurrentContent(),
    )
    signTransfer.assignment.markupState = undefined

    const reqOpts = {
      body: JSON.stringify(signTransfer),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(
      '/api/SignTransfers',
      reqOpts,
    )

    if (result.success === true) {
      const newSignTransfer = SignTransfer.fromJS(result.entity)
      yield put(saveSignTransferSuccess(newSignTransfer))
      yield put(closeSignTransferModal())
      const msg = `Saved transfer of ${newSignTransfer.quantity} - ${
        newSignTransfer.signType.name
      } sign(s)`
      yield put(successToast('Success!', msg))
    } else {
      yield put(saveSignTransferFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveSignTransferFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteSignTransfer() {
  yield takeEvery(ActionTypes.DeleteSignTransfer, deleteSignTransferSaga)
}

function* watchSaveSignTransfer() {
  yield takeLatest(ActionTypes.SaveSignTransfer, saveSignTransferSaga)
}

export function* signTransferRootSaga() {
  yield fork(watchDeleteSignTransfer)
  yield fork(watchSaveSignTransfer)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): SignTransferState =>
  state.signTransfer

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsOpen = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isOpen,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )

export const selectMaxQuantity = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.maxQuantity,
  )

export const selectSignTransfer = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.signTransfer,
  )
